import { lazy, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { ThemeProvider } from '@/context/ThemeContext';
import { SessionProvider } from '@/context/SessionContext';
import { ModalHub } from '@/components/shared/ModalHub';
import { DevToolbar } from '@/components/shared/DevToolbar';
import { ProtectedRoute } from '@/components/shared/ProtectedRoute';
import {
  guardInstruction,
  guardStage1,
  guardBetweenStages,
  guardStage2,
  guardContacts,
  guardLoading,
  guardResult,
  guardConfirmation,
  guardError,
} from '@/components/shared/route-guards';
import { initMaxBridge, getMaxBridge } from '@/lib/max-bridge';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useSession } from '@/hooks/useSession';

import Welcome from '@/screens/Welcome';
import ChildInfo from '@/screens/ChildInfo';
import Instruction from '@/screens/Instruction';
import ContactsForm from '@/screens/ContactsForm';
import Loading from '@/screens/Loading';
import Confirmation from '@/screens/Confirmation';
import ErrorScreen from '@/screens/Error';
import InvalidLink from '@/screens/InvalidLink';

/**
 * Лазим тяжёлые экраны теста отдельными чанками — каждый тащит свою data/X-Y
 * (Stage 1/2 questions, Stage 2 forced-choice cards), что без code-splitting
 * раздуло бы main bundle. К моменту, когда пользователь добирается до экрана,
 * чанк уже подгружен фоном (родитель проводит ребёнка через Welcome → ChildInfo
 * → Instruction — это минимум 30+ секунд на slow 3G).
 *
 *  - Stage1Question / BetweenStages / Stage2Question — экраны теста
 *    (data 9-11 + 12-14 импортируется через @/data).
 *  - Result — отдельно ради recharts (~100kb gzip).
 */
const Stage1Question = lazy(() => import('@/screens/Stage1Question'));
const BetweenStages = lazy(() => import('@/screens/BetweenStages'));
const Stage2Question = lazy(() => import('@/screens/Stage2Question'));
const Result = lazy(() => import('@/screens/Result'));

/**
 * React Router v7 future flags — закрывают консольные warnings v6→v7
 * и активируют поведение, которое будет дефолтным в v7.
 *  - v7_startTransition: useTransition для роут-переходов (плавнее, без flicker).
 *  - v7_relativeSplatPath: новые правила относительных путей внутри splat-роутов.
 */
const ROUTER_FUTURE = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
} as const;

/**
 * Внутренний компонент: обёртка вокруг Routes для трекинга app_open
 * после инициализации SessionProvider.
 *
 * `app_open` событие в финальной форме (UX-спека v1.2 раздел 8 + расширение
 * Phase 2, см. discrepancies_v1.md №19):
 *   { platform, start_param, has_active_session, city }
 *  - platform: из MAX Bridge (ios | android | desktop | web)
 *  - start_param: исходное значение из MAX Bridge без парсинга
 *  - has_active_session: есть ли в state ответы или ребёнок (т.е. сессия
 *    была восстановлена из localStorage с реальным прогрессом)
 *  - city: автоматически из useAnalytics через pre-payload
 */
function AppShell() {
  const { state } = useSession();
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const location = useLocation();

  // Guard на city=null (real-mode без валидного start_param). Срабатывает
  // ДО app_open и ДО любого test-flow. Родитель тапнул системную кнопку
  // «Открыть» вместо welcome button → /invalid-link, лид не создаётся.
  // Mock-mode сюда не попадает (city всегда non-null через DEFAULT_CITY).
  useEffect(() => {
    if (state.city === null && location.pathname !== '/invalid-link') {
      navigate('/invalid-link', { replace: true });
    }
  }, [state.city, location.pathname, navigate]);

  // Трек app_open — один раз после mount Mini App.
  // useRef-флаг защищает от двойного срабатывания в StrictMode (dev-only
  // double-mount). В продакшене StrictMode выключен, но флаг безвреден.
  const trackedRef = useRef(false);
  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    const bridge = getMaxBridge();
    const hasActiveSession =
      state.child !== null ||
      state.stage1Answers.length > 0 ||
      state.stage2Answers.length > 0;
    track('app_open', {
      platform: bridge.platform,
      start_param: bridge.initDataUnsafe?.start_param ?? '',
      has_active_session: hasActiveSession,
    });
    // state.child/answers детерминированы на момент mount после lazy-init
    // useReducer в SessionProvider — пустой deps безопасен.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Routes>
      {/* Публичные маршруты — без ProtectedRoute. */}
      <Route path="/" element={<Welcome />} />
      <Route path="/child-info" element={<ChildInfo />} />

      {/* Защищённые: каждый predicate проверяет, что state соответствует
          ожиданиям маршрута. См. ProtectedRoute.tsx → guard* функции. */}
      <Route
        path="/instruction"
        element={
          <ProtectedRoute predicate={guardInstruction}>
            <Instruction />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stage1"
        element={
          <ProtectedRoute predicate={guardStage1}>
            <Suspense fallback={<ScreenChunkFallback />}>
              <Stage1Question />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/between-stages"
        element={
          <ProtectedRoute predicate={guardBetweenStages}>
            <Suspense fallback={<ScreenChunkFallback />}>
              <BetweenStages />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stage2"
        element={
          <ProtectedRoute predicate={guardStage2}>
            <Suspense fallback={<ScreenChunkFallback />}>
              <Stage2Question />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts"
        element={
          <ProtectedRoute predicate={guardContacts}>
            <ContactsForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/loading"
        element={
          <ProtectedRoute predicate={guardLoading}>
            <Loading />
          </ProtectedRoute>
        }
      />
      <Route
        path="/result"
        element={
          <ProtectedRoute predicate={guardResult}>
            <Suspense fallback={<ScreenChunkFallback />}>
              <Result />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/confirmation"
        element={
          <ProtectedRoute predicate={guardConfirmation}>
            <Confirmation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/error"
        element={
          <ProtectedRoute predicate={guardError}>
            <ErrorScreen />
          </ProtectedRoute>
        }
      />
      {/* /invalid-link — terminal screen для real-mode без валидного
          start_param. Лид не создаётся, на тест нельзя пройти. */}
      <Route path="/invalid-link" element={<InvalidLink />} />
      <Route path="*" element={<Welcome />} />
    </Routes>
  );
}

/**
 * Минимальный фолбек на время докачивания chunk'а lazy-экрана. Тот же spinner,
 * что в Loading.tsx — визуально продолжает поток. Используется для всех
 * lazy-routes (Stage1, Stage2, BetweenStages, Result).
 */
function ScreenChunkFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <span
        aria-label="Загрузка"
        role="status"
        className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-border border-t-accent"
      />
    </div>
  );
}

export default function App() {
  // Инициализация MAX Bridge (real или mock в зависимости от VITE_MAX_MODE).
  useEffect(() => {
    const bridge = initMaxBridge();
    bridge.ready();
  }, []);

  return (
    <ThemeProvider>
      <SessionProvider>
        <div className="mx-auto max-w-app min-h-screen bg-background">
          <BrowserRouter future={ROUTER_FUTURE}>
            <AppShell />
            {/* ModalHub внутри Router — нужен `useNavigate` для M5 «Открыть результат». */}
            <ModalHub />
          </BrowserRouter>
          {import.meta.env.DEV && <DevToolbar />}
        </div>
      </SessionProvider>
    </ThemeProvider>
  );
}
